/* eslint-disable @typescript-eslint/no-explicit-any */
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantInfoService } from '@vsolv/core/multi-tenant/web';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { SecurityService, StaffService } from '@wsphere/staff/web';
import { Policy } from '@wsphere/warranties/domain';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  CopyTermConfigDialog,
  CreateNewVersionDialog,
  EditPlanConditionDialog,
  ExportPlanCoverageConfigsDialog,
  ManagePlanGroupsDialog,
} from '../../dialogs';
import { PolicyService } from '../../services';
import { DescriptiveColumnsService } from '../../services/descriptive-columns.service';
import { DuplicateFeeHandlerService } from '../../services/duplicate-fee-handler.service';
import { getColumnConfig } from '../new-descriptive-columns-button/descriptive-columns.config';
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-plan-pricing-table',
  templateUrl: './plan-pricing-table.component.html',
  standalone: false,
})
export class PlanPricingTableComponent implements OnInit, OnDestroy {
  portalConfigDetail: any;
  private destroy$ = new Subject<void>();
  private _cachedVisibleColumns: string[] = [];
  private _lastVisibleColumnsSet: Set<string> = new Set();
  private termCoverageCache = new Map<string, Policy.Plan.CoverageConfig[] | null>();
  private termFeesCache = new Map<string, Policy.PlanCoverageFee.Model[] | null>();
  private termLoadInFlight = new Map<string, Promise<void>>();
  loadingTermId: string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private securitySvc: SecurityService,
    private tenantInfoSvc: TenantInfoService,
    private staffSvc: StaffService,
    private breakpointObserver: BreakpointObserver,
    private portalService: PortalService,
    private descriptiveColumnsService: DescriptiveColumnsService,
    private duplicateFeeHandler: DuplicateFeeHandlerService
  ) {}

  @ViewChild('exportCoverageConfigsDialog') exportCoverageConfigsDialog?: ExportPlanCoverageConfigsDialog;

  @ViewChild('pricingMissingInfoDialog') pricingMissingInfoDialog?: DialogComponent;
  @ViewChild('createVersionDialog') createVersionDialog?: CreateNewVersionDialog;

  @ViewChild('configureTermConfigDialog') configureTermConfigDialog?: CopyTermConfigDialog;
  @ViewChild('discardTermConfigDialog') discardTermConfigDialog?: DialogComponent;

  @ViewChild('editConditionDialog') editConditionDialog?: EditPlanConditionDialog;
  @ViewChild('manageGroupsDialog') manageGroupsDialog?: ManagePlanGroupsDialog;

  @ViewChild('duplicateTitleDialog') duplicateTitleDialog?: DialogComponent;
  @ViewChild('removeFeeDialog') removeFeeDialog?: DialogComponent;
  @ViewChild('removeDescriptiveColumnDialog') removeDescriptiveColumnDialog?: DialogComponent;

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy: Policy.Model | null = null;

  plan$ = new BehaviorSubject<Policy.Plan.Model | null>(null);

  @Input() set plan(value: Policy.Plan.Model | null) {
    if (!value) {
      this.plan$.next(null);
      return;
    }

    const current = this.plan$.getValue();
    if (current?.id === value.id) {
      // Parent refreshes (e.g. after saving plan details) often omit coverageConfigs.
      // Preserve loaded term configs and in-memory edits instead of wiping the pricing table.
      const mergedCoverageConfigs: Policy.Plan.PlanCoverageConfigs = { ...(value.coverageConfigs ?? {}) };

      for (const termId of Object.keys(current.coverageConfigs ?? {})) {
        const existing = current.coverageConfigs?.[termId];
        if (existing?.length) {
          mergedCoverageConfigs[termId] = existing;
        }
      }

      for (const [termId, cached] of this.termCoverageCache.entries()) {
        if (cached?.length) {
          mergedCoverageConfigs[termId] = cached;
        }
      }

      this.plan$.next({ ...value, coverageConfigs: mergedCoverageConfigs });
      return;
    }

    this.plan$.next({
      ...value,
      coverageConfigs: value.coverageConfigs ?? ({} as Policy.Plan.PlanCoverageConfigs),
    });
  }

  get plan(): Policy.Plan.Model | null {
    return this.plan$.getValue();
  }

  @PropertyListener('propertySet') propertySet$ = new BehaviorSubject<PropertySet.Model | null>(null);
  @Input() propertySet: PropertySet.Model | null = null;

  @Input() canEditPlans = false;

  @Input() dynamicRatingEnabled: boolean | null = false;

  @PropertyListener('selectedTermId') selectedTermId$ = new BehaviorSubject<string | null>(null);
  @Input() set selectedTermId(value: string | null) {
    if (value !== this.selectedTermId$.getValue()) {
      this.selectedTermId$.next(value);
    }
  }
  get selectedTermId(): string | null {
    return this.selectedTermId$.getValue();
  }

  @PropertyListener('selectedFees') selectedFees$ = new BehaviorSubject<Policy.PlanCoverageFee.Model[] | null>(null);
  selectedFees: Policy.PlanCoverageFee.Model[] | null = null;

  editing = false;
  saving = false;

  selectedPlanId = '';

  savePricingList = '';

  actionFeeName = '';
  actionFees: Policy.PlanCoverageFee.Model[] | null = null;

  actionColumnType: string | null = null;
  actionColumnName = '';

  refreshCosts$ = new BehaviorSubject(null);

  // Descriptive columns visibility (dynamic)
  visibleColumns: Set<string> = new Set();

  readonly isSmall$ = this.breakpointObserver.observe([Breakpoints.XSmall]).pipe(map(state => state.matches));

  selectedTerm$ = combineLatest([this.selectedTermId$, this.policy$]).pipe(
    map(([termId, policy]) => {
      if (!policy) return null;
      const hit = policy.terms.find(term => term.id === termId);
      if (hit) return hit;

      // Try to find the same term in the new policy version by matching base ID (strip version
      // suffix, e.g. 'trm_4yr-14' → 'trm_4yr'). This keeps the user on the same term tab after
      // a new version is created instead of jumping to the first tab. vs-tabs has no selectedIndex
      // binding and preserves its active tab index, so loading the wrong term causes the visible
      // tab to display a loading skeleton that never resolves.
      const baseId = termId?.split('-')[0];
      const sameTermNewVersion = baseId ? policy.terms.find(term => term.id.split('-')[0] === baseId) : null;
      const fallback = sameTermNewVersion ?? policy.terms[0];
      if (fallback && termId !== fallback.id) {
        // reset to the matched (or first available) term
        this.selectedTermId$.next(fallback.id);
      }
      return fallback ?? null;
    }),
    tap(term => {
      // Update descriptive columns visibility when term changes.
      // Guard against a stale plan: when policy$ emits before plan$ (they are set in template
      // order), this.plan still references the old plan. Preloading with the old plan would
      // fetch coverage configs for the wrong plan ID and emit stale data through plan$.
      const plan = this.plan;
      if (term?.id && plan && (!this.policy || plan.policyId === this.policy.id)) {
        this.updateDescriptiveColumnsVisibility(term.id);
        void this.preloadTermCoverage(plan, term.id);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  canEditFees = false;
  canEditFees$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const globalDistIds = globalDistributors ? globalDistributors?.map(dist => dist.id) : null;
      return await this.securitySvc.hasAccess('pol_FeeSetup', globalDistIds);
    }),
    tap(canEditFees => (this.canEditFees = canEditFees)),
    takeUntil(this.destroy$)
  );

  fees$ = combineLatest([this.plan$, this.selectedTermId$]).pipe(
    filter(([plan, termId]) => !!plan && !!termId),
    distinctUntilChanged((a, b) => {
      const [planA, termIdA] = a;
      const [planB, termIdB] = b;
      return planA?.id === planB?.id && termIdA === termIdB;
    }),
    switchMap(async ([plan, termId]) => {
      if (!plan || !termId) return [];

      if (!this.selectedPlanId.length) this.selectedPlanId = plan.id;
      else if (this.selectedPlanId !== plan?.id) {
        this.editing = false;
        this.selectedPlanId = plan.id;
      }

      // Serve from cache if available
      if (this.termFeesCache.has(termId)) {
        this.selectedFees = this.cloneFees(this.termFeesCache.get(termId) ?? []);
        this.refreshCosts$.next(null);
        return this.selectedFees;
      }

      const allFees = await this.fetchAllFees(plan, termId);
      if (!allFees.length) {
        this.refreshCosts$.next(null);
        this.setCachedSelectedFees(termId, []);
        return [];
      }

      // Process actual duplicates (same fee type/title for same coverage) but not cross-coverage fees
      this.setCachedSelectedFees(termId, this.duplicateFeeHandler.processDuplicatesAutomatically(allFees));

      this.refreshCosts$.next(null);
      return this.selectedFees ?? [];
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  feesAndTypes$ = this.selectedFees$.pipe(
    map(selectedFees => {
      const plan = this.plan,
        termId = this.selectedTermId;
      if (!plan || !termId || !selectedFees) return [];

      // Index fees for fast lookup
      const feeMap = new Map<string, Policy.PlanCoverageFee.Model>();
      for (const fee of selectedFees) {
        feeMap.set(`${fee.feeType}||__||${fee.title}||__||${fee.coverageId}||__||${fee.termId}`, fee);
      }

      // Get unique fee types (ignoring coverage - fees should be columns, not per-coverage)
      const feeTypes = this.duplicateFeeHandler.getUniqueFeeTypesWithDuplicates(selectedFees);

      const coverageIds = plan.coverageConfigs[termId]?.map(config => config.coverageId) || [];

      return feeTypes.map(feeType => ({
        type: feeType,
        fees: coverageIds.map(coverageId => {
          // Extract feeType and title from the feeType string
          const [feeTypeValue, fullTitle] = feeType.split('||__||');

          // Look for exact fee match (including numbered duplicates)
          const found = selectedFees.find(
            fee =>
              fee.feeType === feeTypeValue &&
              fee.coverageId === coverageId &&
              fee.termId === termId &&
              fee.title === fullTitle
          );

          if (found) return found;

          return {
            id: '',
            termId,
            coverageId,
            planId: plan.id,
            policyId: plan.policyId,
            title: fullTitle,
            feeType: feeTypeValue as Policy.PlanCoverageFee.Model['feeType'],
            rule: null,
          };
        }),
      }));
    })
  );

  costs$ = combineLatest([this.plan$, this.selectedTerm$, this.selectedFees$]).pipe(
    map(([plan, term, selectedFees]) => {
      if (!plan || !plan.coverageConfigs || !term || !selectedFees) return [];

      // Index fees by coverageId and termId
      const feeMap = new Map<string, Policy.PlanCoverageFee.Model[]>();
      for (const fee of selectedFees) {
        if (fee.termId !== term.id) continue;
        const arr = feeMap.get(fee.coverageId) || [];
        arr.push(fee);
        feeMap.set(fee.coverageId, arr);
      }

      const coverageIds = plan.coverageConfigs[term.id]?.map(config => config.coverageId) || [];

      return coverageIds.map(coverageId => {
        const fees = feeMap.get(coverageId) || [];

        // Sum all fees for this coverage - fees are stored in cents, so we sum cents
        const costInCents = fees.reduce((acc, fee) => {
          const feeValue = fee.rule?.defaultValue || 0;
          // Ensure we're working with numbers and handle edge cases
          if (typeof feeValue !== 'number' || isNaN(feeValue)) {
            console.warn(`Invalid fee value for coverage ${coverageId}, fee ${fee.feeType}:`, feeValue);
            return acc;
          }
          return acc + feeValue;
        }, 0);

        // Return cost in cents to maintain consistency with the monetary box component
        return { coverageId, cost: costInCents };
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Cache per-term configs/fees to avoid refetching
  private async preloadTermCoverage(plan: Policy.Plan.Model, termId: string): Promise<void> {
    if (!termId) return;
    if (!plan.coverageConfigs) plan.coverageConfigs = {} as NonNullable<Policy.Plan.Model['coverageConfigs']>;

    // Serve from cache (preserve null to indicate no configuration)
    if (this.termCoverageCache.has(termId)) {
      const cached = this.termCoverageCache.get(termId) ?? null;
      plan.coverageConfigs[termId] = cached;
      this.emitPlanCoverageUpdate(plan);
      return;
    }

    // De-dupe concurrent loads
    const existing = this.termLoadInFlight.get(termId);
    if (existing) {
      await existing;
      const cached = this.termCoverageCache.get(termId) ?? null;
      plan.coverageConfigs[termId] = cached;
      this.emitPlanCoverageUpdate(plan);
      return;
    }

    const loadPromise = (async () => {
      this.loadingTermId = termId;
      const termPlan = await this.policySvc.getPlan(plan.policyId, plan.id, {
        includeCoverageConfigs: true,
        includeFees: false,
        termId,
        includeCoverageConfigBlocks: true,
      });
      const configs = termPlan?.coverageConfigs?.[termId] ?? null;
      this.termCoverageCache.set(termId, configs);
      plan.coverageConfigs[termId] = configs;
      this.emitPlanCoverageUpdate(plan);
    })();

    this.termLoadInFlight.set(termId, loadPromise);
    await loadPromise.finally(() => {
      this.termLoadInFlight.delete(termId);
      this.loadingTermId = null;
    });
  }

  /**
   * Coverage configs are loaded asynchronously per-term and mutated in-place.
   * Emit fresh references so downstream streams (fees/costs columns) recompute on plan navigation.
   */
  private emitPlanCoverageUpdate(plan: Policy.Plan.Model) {
    // Guard: if the plan has changed since this async fetch started (e.g. the user navigated to
    // a different plan), do not emit stale plan data. Emitting an old plan ID through plan$
    // would bypass distinctUntilChanged in the plan$ subscription, clear the cache, and trigger
    // a cascade of re-fetches against the wrong plan.
    if (plan.id !== this.plan$.getValue()?.id) return;

    this.plan$.next({
      ...plan,
      coverageConfigs: { ...(plan.coverageConfigs || {}) },
    });

    // Only emit fees scoped to the currently selected term to avoid flashing stale columns on term switches.
    const termId = this.selectedTermId;
    if (!termId) return;

    if (this.termFeesCache.has(termId)) {
      const cachedFeesForTerm = this.termFeesCache.get(termId) ?? null;
      this.selectedFees = this.cloneFees(cachedFeesForTerm);
      return;
    }

    const scopedFees = (this.selectedFees || []).filter(fee => fee.termId === termId);
    this.selectedFees = this.cloneFees(scopedFees);
  }

  isTermLoaded(termId: string | null | undefined): boolean {
    if (!termId) return false;
    return this.termCoverageCache.has(termId) || !!this.plan?.coverageConfigs?.[termId]?.length;
  }

  openManageGroupsDialog() {
    this.manageGroupsDialog?.open();
  }

  openExportCoverageConfigsDialog() {
    this.exportCoverageConfigsDialog?.open();
  }

  getTermCoverageConfigs(termId: string | null | undefined): Policy.Plan.CoverageConfig[] {
    if (!termId) return [];
    return this.termCoverageCache.get(termId) ?? this.plan?.coverageConfigs?.[termId] ?? [];
  }

  /**
   * Fetch all fees for a given term, paging until completion.
   * Uses meta.totalPages when available; otherwise stops when the page size is below the limit.
   */
  private async fetchAllFees(plan: Policy.Plan.Model, termId: string): Promise<Policy.PlanCoverageFee.Model[]> {
    const all: Policy.PlanCoverageFee.Model[] = [];
    const limit = 100;
    let page = 1;
    const maxPages = 20; // safety cap to avoid runaway loops

    while (page <= maxPages) {
      const res = await this.policySvc.getPlanFees(plan.policyId, plan.id, termId, { page, limit });
      const items = res?.items ?? [];
      if (!items.length) break;
      all.push(...items);

      const totalPages = res?.meta?.totalPages;
      if (totalPages && page >= totalPages) break;
      if (!totalPages && items.length < limit) break;

      page += 1;
    }

    return all;
  }

  private cloneFees(fees: Policy.PlanCoverageFee.Model[] | null): Policy.PlanCoverageFee.Model[] {
    return fees?.map(fee => ({ ...fee })) ?? [];
  }

  private setCachedSelectedFees(termId: string, fees: Policy.PlanCoverageFee.Model[] | null) {
    const nextFees = this.cloneFees(fees);
    this.termFeesCache.set(termId, nextFees);
    this.selectedFees = nextFees;
  }

  private getNextFeeTitle(
    feeType: Policy.PlanCoverageFee.Fee,
    termId: string,
    existingFees: Policy.PlanCoverageFee.Model[]
  ): string {
    const baseTitle = this.getFeeName(feeType);
    const existingTitles = new Set(
      existingFees.filter(fee => fee.termId === termId && fee.feeType === feeType).map(fee => fee.title)
    );

    if (!existingTitles.has(baseTitle)) return baseTitle;

    let duplicateNumber = 2;
    while (existingTitles.has(`${baseTitle} (${duplicateNumber})`)) {
      duplicateNumber += 1;
    }

    return `${baseTitle} (${duplicateNumber})`;
  }

  private resolveTargetTermId(preferred?: string | null): string | null {
    return preferred || this.selectedTermId || this.policy?.terms?.[0]?.id || null;
  }
  openConfigureTermDialog(termId?: string) {
    if (!this.plan) return;
    const resolvedTermId = this.resolveTargetTermId(termId);

    if (!resolvedTermId) {
      // No valid term could be resolved; do not open the dialog without a term context.
      return;
    }
    this.selectedTermId = resolvedTermId;
    this.configureTermConfigDialog?.open(this.plan);
  }

  openDiscardTermConfigDialog() {
    this.discardTermConfigDialog?.open();
  }

  async ngOnInit() {
    this.plan$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        takeUntil(this.destroy$)
      )
      .subscribe(plan => {
        this.termCoverageCache.clear();
        this.termFeesCache.clear();
        this.termLoadInFlight.clear();

        // When switching plans, ensure a term is selected and preload it
        const firstTermId = this.policy?.terms?.[0]?.id || null;

        // Reset selectedTermId if it no longer exists in the current policy's terms.
        // This happens when a new policy version is created (term IDs get a new version suffix),
        // otherwise preloadTermCoverage runs with a stale term ID, caches a null entry, and the
        // loading skeleton persists because the template iterates the new policy's terms.
        if (
          this.selectedTermId &&
          this.policy?.terms?.length &&
          !this.policy.terms.some(t => t.id === this.selectedTermId)
        ) {
          this.selectedTermId = null;
        }

        if (!this.selectedTermId && firstTermId) {
          this.selectedTermId = firstTermId;
        }
        const termToLoad = this.selectedTermId || firstTermId;
        if (plan && termToLoad) {
          void this.preloadTermCoverage(plan, termToLoad);
        }
      });

    // Fetch tenant config
    const tenantId = this.tenantInfoSvc.getLocalTenantId();
    if (tenantId) {
      this.portalConfigDetail = await this.portalService.getConfig(tenantId);
    }
    // Set default term if not already set
    if (!this.selectedTermId && this.policy?.terms?.length) {
      this.selectedTermId = this.policy.terms[0].id;
    }

    // Subscribe to selectedFees$ for basic tracking (no automatic changes)
    this.selectedFees$
      .pipe(
        takeUntil(this.destroy$),
        filter(fees => fees !== null && fees.length > 0)
      )
      .subscribe(_fees => {
        // Just track that fees are loaded, don't modify them automatically
      });
  }

  async configureTerm(
    termId: string,
    configs?: Policy.Plan.CoverageConfig[],
    fees?: Policy.PlanCoverageFee.Model[],
    done?: () => void,
    error?: (err: any) => void
  ) {
    try {
      if (!this.policy || !this.policy.coverages || !this.plan) {
        if (done) {
          done();
        }
        return;
      }

      const targetTermId = this.resolveTargetTermId(termId);
      if (!targetTermId) {
        if (done) done();
        return;
      }

      this.editing = true;

      const nextConfigs =
        configs ??
        this.policy.coverages.map(coverage => ({
          coverageId: coverage.id,
          requirement: { defaultValue: null, blocks: [] } as Conditions.Rule<any>,
          deductible: { defaultValue: null, blocks: [] } as Conditions.Rule<any>,
          liabilityLimit: { defaultValue: null, blocks: [] } as Conditions.Rule<any>,
          price: { defaultValue: null, blocks: [] } as Conditions.Rule<any>,
          liabilityGroups: { defaultValue: [], blocks: [] } as Conditions.Rule<any>,
          sku: { defaultValue: '', blocks: [] } as Conditions.Rule<any>,
          skuDescription: { defaultValue: '', blocks: [] } as Conditions.Rule<any>,
        }));

      this.plan.coverageConfigs[targetTermId] = nextConfigs;
      // Keep cache aligned so UI doesn't continue rendering stale empty cached rows.
      this.termCoverageCache.set(targetTermId, nextConfigs);

      if (fees) {
        this.setCachedSelectedFees(
          targetTermId,
          this.duplicateFeeHandler.processDuplicatesAutomatically(
            fees.map(fee => ({ ...fee, termId: targetTermId, id: '' }))
          )
        );
      } else {
        this.setCachedSelectedFees(targetTermId, []);
      }

      // Check if descriptive columns should be visible by default
      this.updateDescriptiveColumnsVisibility(targetTermId);
      this.selectedTermId = targetTermId;

      if (configs) {
        await this.save();
      }
      if (done) {
        done();
      }
    } catch (err) {
      if (error) {
        error(err);
      }
    }
  }

  async removeTermConfig(termId: string) {
    if (!this.plan) return;

    this.plan.coverageConfigs[termId] = null;
    this.termCoverageCache.set(termId, null);
    this.termFeesCache.delete(termId);
    this.selectedFees = this.selectedFees?.filter(fee => fee.termId !== termId) ?? null;

    await this.save();
  }

  editPricing() {
    if (this.policy?.status === Policy.Status.DRAFT) this.editing = true;
    else this.createVersionDialog?.open();
  }

  findCoverage(config: Policy.Plan.CoverageConfig) {
    return this.policy?.coverages?.find(coverage => coverage.id === config.coverageId);
  }

  updateConfig(
    termId: string,
    coverageId: string,
    rule: Conditions.Rule<string | number | string[]> | null,
    type: string
  ) {
    if (!rule || !coverageId || !this.plan || !this.selectedTermId || !this.plan.coverageConfigs[termId]?.length) {
      return;
    }

    const selectedConfig = this.plan?.coverageConfigs[termId];

    if (!selectedConfig) return;
    const configIndex = selectedConfig.findIndex(config => config.coverageId === coverageId);

    switch (type) {
      case 'requirement':
        selectedConfig[configIndex].requirement = rule as Conditions.Rule<string>;
        break;
      case 'deductible':
        selectedConfig[configIndex].deductible = rule as Conditions.Rule<number>;
        break;
      case 'liabilityLimit':
        selectedConfig[configIndex].liabilityLimit = rule as Conditions.Rule<number>;
        break;
      case 'price':
        selectedConfig[configIndex].price = rule as Conditions.Rule<number>;
        break;
      case 'liabilityGroups':
        selectedConfig[configIndex].liabilityGroups = this.normalizeLiabilityGroupRule(rule as Conditions.Rule<any>);
        break;
      case 'sku':
      case 'skuDescription': {
        // Handle descriptive columns generically - ensure string type
        const stringRule = rule as Conditions.Rule<string>;
        this.descriptiveColumnsService.updateColumnField(selectedConfig[configIndex], type, stringRule);
        break;
      }
    }

    this.plan.coverageConfigs[termId] = [...selectedConfig];
  }

  findCost(costs: { coverageId: string; cost: number }[] | null, coverageId: string) {
    if (!costs?.length) return 0;

    const costEntry = costs.find(cost => cost.coverageId === coverageId);
    if (!costEntry) return 0;

    // Cost is in cents from the costs$ observable, convert to dollars for display
    // The monetary box component displays config.text directly with currency pipe
    const costInDollars = costEntry.cost / 100;

    return costInDollars;
  }

  getFeeName(name: string) {
    return name[0] + name.substring(1).replace(/_/g, ' ').toLowerCase();
  }

  updateFeeTitle(type: string, oldTitle: string, newTitle: string, termId: string) {
    const currentTitles = this.selectedFees?.reduce((acc, fee) => {
      if (!acc.includes(fee.feeType + fee.title) && fee.termId === termId) acc.push(fee.feeType + fee.title);
      return acc;
    }, [] as string[]);

    if (currentTitles?.includes(type + newTitle)) {
      this.actionFeeName = newTitle;
      this.duplicateTitleDialog?.open();
    } else {
      this.setCachedSelectedFees(
        termId,
        (this.selectedFees ?? []).map(fee => {
          if (fee.feeType + fee.title === type + oldTitle && fee.termId === termId) {
            return { ...fee, title: newTitle };
          }

          return fee;
        })
      );
    }
  }

  addFees(fees: Policy.PlanCoverageFee.Fee[], termId: string) {
    if (!this.plan || !this.policy) return;

    const plan = this.plan;
    const policy = this.policy;

    const coverageIds = plan.coverageConfigs[termId]?.map(config => config.coverageId) ?? [];
    if (!coverageIds.length) return;

    const nextFees = this.cloneFees(this.selectedFees);

    fees.forEach(feeType => {
      const feeTitle = this.getNextFeeTitle(feeType, termId, nextFees);

      coverageIds.forEach(coverageId => {
        nextFees.push({
          id: '',
          policyId: policy.id,
          planId: plan.id,
          termId,
          coverageId,
          feeType,
          title: feeTitle,
          rule: { defaultValue: 0, blocks: [] },
        });
      });
    });

    this.setCachedSelectedFees(termId, nextFees);
  }

  /**
   * Show all duplicate fees by creating visible columns for them
   * This helps users see exactly where their cost total is coming from
   */
  showAllDuplicateFees() {
    // This method calls the automatic processing which now shows ALL duplicates
    this.selectedFees = this.duplicateFeeHandler.processDuplicatesAutomatically(this.selectedFees || []);
    this.selectedFees$.next(this.selectedFees);
  }

  /**
   * Check if there are any duplicate fees that could be causing cost discrepancies
   */
  hasDuplicateFees(): boolean {
    return this.duplicateFeeHandler.hasDuplicateFees(this.selectedFees || []);
  }

  private normalizeLiabilityGroupRule(rule: Conditions.Rule<any>): Conditions.Rule<string[]> {
    const normalize = (value: string[] | string | null | undefined): string[] => {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (typeof value === 'string') return value ? [value] : [];
      return [];
    };

    return {
      defaultValue: normalize(rule?.defaultValue as any),
      blocks:
        rule?.blocks?.map(block => ({
          ...block,
          value: normalize(block.value as any),
        })) ?? [],
      valuePropertyPath: rule?.valuePropertyPath ?? null,
    };
  }

  updateFeeRule(updatedFee: Policy.PlanCoverageFee.Model, rule: Conditions.Rule<any> | null) {
    if (!this.plan) return;

    const planFee = this.selectedFees?.find(
      fee =>
        fee.id === updatedFee.id &&
        fee.termId === updatedFee.termId &&
        fee.coverageId === updatedFee.coverageId &&
        fee.feeType === updatedFee.feeType &&
        fee.title === updatedFee.title
    );
    if (!planFee) return;

    planFee.rule = rule;
  }

  removeFeeGroup() {
    if (!this.plan || !this.actionFees?.length) return;

    const actionTermId = this.actionFees[0].termId;
    const feeIds = this.actionFees.map(fee => fee.id);
    if (!feeIds.includes('')) {
      this.setCachedSelectedFees(actionTermId, this.selectedFees?.filter(fee => !feeIds.includes(fee.id)) ?? []);
    } else {
      this.setCachedSelectedFees(
        actionTermId,
        this.selectedFees?.filter(
          fee => fee.feeType + fee.title !== (this.actionFees?.[0].feeType || '') + this.actionFees?.[0].title
        ) ?? []
      );
    }

    this.actionFeeName = '';
    this.actionFees = null;
    this.removeFeeDialog?.close();
  }

  removeDescriptiveColumn() {
    if (!this.plan || !this.selectedTermId || !this.actionColumnType) return;

    const termConfigs = this.plan.coverageConfigs[this.selectedTermId];
    if (!termConfigs) return;

    // Clear the data for the column being removed
    this.descriptiveColumnsService.clearColumnData(termConfigs, this.actionColumnType);

    // Hide the column
    this.visibleColumns.delete(this.actionColumnType);

    // Clean up
    this.actionColumnType = null;
    this.actionColumnName = '';
    this.removeDescriptiveColumnDialog?.close();
  }

  // Helper methods for template
  isColumnVisible(columnKey: string): boolean {
    return this.visibleColumns.has(columnKey);
  }

  getColumnConfig(columnKey: string) {
    return getColumnConfig(columnKey);
  }

  /**
   * Check if a column key is a descriptive column (SKU or SKU Description)
   */
  isDescriptiveColumn(columnKey: string): boolean {
    return ['sku', 'skuDescription'].includes(columnKey);
  }

  getColumnRule(coverageConfig: Policy.Plan.CoverageConfig, columnKey: string): Conditions.Rule<any> {
    return (
      this.descriptiveColumnsService.getColumnField(coverageConfig, columnKey) || { defaultValue: null, blocks: [] }
    );
  }

  getVisibleColumnKeys(): string[] {
    // Only recalculate if the Set has changed
    if (
      this._lastVisibleColumnsSet.size !== this.visibleColumns.size ||
      !Array.from(this._lastVisibleColumnsSet).every(col => this.visibleColumns.has(col))
    ) {
      this._cachedVisibleColumns = Array.from(this.visibleColumns);
      this._lastVisibleColumnsSet = new Set(this.visibleColumns);
    }
    return this._cachedVisibleColumns;
  }

  updateDescriptiveColumnValue(termId: string, coverageId: string, event: Event, columnKey: string) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const rule: Conditions.Rule<string> = {
      defaultValue: value,
      blocks: [],
    };

    this.updateConfig(termId, coverageId, rule, columnKey);
  }

  saveCheck() {
    if (!this.selectedTermId) return;
    if (this.policy?.status !== Policy.Status.DRAFT) return;

    this.savePricingList = '';

    let requirement = false;
    let deductible = false;
    let liability = false;
    let price = false;

    this.plan?.coverageConfigs[this.selectedTermId]?.forEach(config => {
      if (!config.requirement.defaultValue) requirement = true;
      if (!config.deductible.defaultValue && config.deductible.defaultValue !== 0) deductible = true;
      if (!config.liabilityLimit.defaultValue && config.liabilityLimit.defaultValue !== 0) liability = true;
      if (!this.dynamicRatingEnabled && !config.price.defaultValue && config.price.defaultValue !== 0) price = true;
    });

    if (requirement || deductible || liability || price) {
      this.savePricingList += '<br><br><ul>';
      if (requirement) this.savePricingList += '<li>Included in</li>';
      if (deductible) this.savePricingList += '<li>Deductible</li>';
      if (liability) this.savePricingList += '<li>Limit of liability</li>';
      if (price) this.savePricingList += '<li>Price</li>';
      this.savePricingList += '</ul><br>';

      this.pricingMissingInfoDialog?.open();
    } else this.save();
  }

  addDescriptiveColumns(columnKeys: string[]) {
    if (!this.plan || !this.selectedTermId) return;

    // Show the selected columns
    columnKeys.forEach(columnKey => {
      this.visibleColumns.add(columnKey);
    });

    // Initialize the fields in the coverage configs if they don't exist
    const termConfigs = this.plan.coverageConfigs[this.selectedTermId];
    if (termConfigs) {
      this.descriptiveColumnsService.initializeColumnFields(termConfigs, columnKeys);
    }
  }

  updateDescriptiveColumnsVisibility(termId: string) {
    if (!this.plan?.coverageConfigs[termId]) {
      this.visibleColumns.clear();
      return;
    }

    const configs = this.plan.coverageConfigs[termId];
    if (!configs) {
      this.visibleColumns.clear();
      return;
    }

    // Get columns that have data and make them visible
    const columnsWithData = this.descriptiveColumnsService.getColumnsWithData(configs);
    this.visibleColumns = new Set(columnsWithData);
  }

  async save() {
    if (
      !this.editing ||
      !this.plan ||
      !this.policy ||
      !this.selectedTermId ||
      this.policy.status !== Policy.Status.DRAFT
    ) {
      return;
    }

    this.saving = true;

    const plan = await this.policySvc
      .updatePlanTerm(this.plan.id, this.selectedTermId, this.policy.id, {
        canCreateNewVersion: false,
        coverages: this.plan.coverageConfigs[this.selectedTermId] || null,
        fees: this.canEditFees ? this.selectedFees || [] : undefined,
      })
      .catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });
      });

    const updatedPlan = plan && typeof plan === 'object' ? plan : undefined;
    const planId = updatedPlan && 'id' in updatedPlan ? updatedPlan.id : undefined;
    if (planId && updatedPlan) {
      if (this.policy.id === updatedPlan.policyId) {
        this.toastSvc.show({
          type: 'success',
          title: 'Updated <strong>' + this.plan.title + '</strong>',
          text: '<strong>' + this.plan.title + '</strong> has been successfully updated.',
        });

        this.policySvc.refreshPlan(undefined, { includeCoverageConfigs: false, includeFees: false });
        this.policySvc.refreshPolicy();
      } else {
        this.toastSvc.show({
          type: 'success',
          title: 'Updated plan',
          text:
            'A new version of <strong>' +
            this.policy.title +
            '</strong> has been created with an updated version of <strong>' +
            this.plan.title +
            '</strong>.',
        });

        this.policySvc.refreshPolicy(updatedPlan.policyId);

        const newPolicy = await this.policySvc.getOne(updatedPlan.policyId);
        if (!newPolicy) throw new Error(`Unable to find policy with id: ${updatedPlan.policyId}`);
        const newPlanId =
          newPolicy.plans?.find(plan => plan.id.startsWith(this.plan?.id?.split('-')[0] || ''))?.id || '';

        this.navigateTo('../' + updatedPlan.policyId + '/plans/' + newPlanId);
      }

      this.pricingMissingInfoDialog?.close();

      this.editing = false;
      this.saving = false;
    }
  }

  navigateTo(path?: any) {
    if (path) this.router.navigate([`${path}`], { relativeTo: this.route });
  }
  trackByFeeType(index: number, group: { type: string }) {
    return group.type;
  }
  trackByCoverageId(index: number, fee: Policy.PlanCoverageFee.Model) {
    return fee.coverageId;
  }
  trackByTermId(index: number, term: any) {
    return term.id;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up BehaviorSubjects
    this.policy$.complete();
    this.plan$.complete();
    this.propertySet$.complete();
    this.selectedFees$.complete();
    this.refreshCosts$.complete();
  }
}
